import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export interface DeletionCounts {
  users: number;
  locations: number;
  products: number;
  inventory: number;
  sales: number;
  reservations: number;
  carts: number;
  quotes: number;
  invoices: number;
  pricingTiers: number;
  stockTransfers: number;
  usageRecords: number;
  b2bAccessRequests: number;
  resellerProfiles: number;
  deliveries: number;
  deliveryRoutes: number;
  inventoryMovements: number;
}

export interface DeletionImpact {
  organization: {
    id: string;
    name: string;
  };
  counts: DeletionCounts;
  totalRecords: number;
  canDelete: boolean;
  warnings: string[];
}

export interface DeletedOrganization {
  id: string;
  name: string;
  slug: string;
  deletedAt: string;
  createdAt: string;
}

const QUERY_KEYS = {
  deletionImpact: (orgId: string) => ['organizations', orgId, 'deletion-impact'] as const,
  trash: ['organizations', 'trash'] as const,
  organizations: ['organizations'] as const,
};

export const useDeletionImpact = (orgId: string, enabled = false) => {
  return useQuery({
    queryKey: QUERY_KEYS.deletionImpact(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<DeletionImpact>(
        `/organizations/${orgId}/deletion-impact`
      );
      return data;
    },
    enabled: !!orgId && enabled,
  });
};

export const useSoftDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      await axiosInstance.delete(`/organizations/${orgId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.organizations });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
    },
  });
};

export const useRestoreOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      await axiosInstance.post(`/organizations/${orgId}/restore`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.organizations });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
    },
  });
};

export const usePermanentDeleteOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      await axiosInstance.delete(`/organizations/${orgId}/permanent`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash });
    },
  });
};

export const useTrashedOrganizations = () => {
  return useQuery({
    queryKey: QUERY_KEYS.trash,
    queryFn: async () => {
      const { data } = await axiosInstance.get<DeletedOrganization[]>(
        '/organizations/trash/list'
      );
      return data;
    },
  });
};
